import { memo } from 'react';

import { SharedActionsMenuProps } from '../../types/SharedActionsMenuProps';
import { ButtonSize } from '../../types/SharedButtonProps';
import { SharedIconProps } from '../../types/SharedIconProps';
import { SharedTooltipProps } from '../../types/SharedTooltipProps';
import { Button } from '../Button/Button';
import { IconButton } from '../Button/subcomponents/IconButton';
import '../Button/Button.css';
import { Menu } from '../Menu/Menu';

type ActionsMenuProps = SharedActionsMenuProps & {
  /** The trigger for the menu. */
  icon: SharedIconProps;
  /** Id for the trigger element. */
  id?: string;
  /**
   * Accessible name for the open menu. Omit to inherit the trigger's name;
   * set it when several identically named triggers each open their own menu.
   */
  label?: string;
  /**
   * Trigger size. Set this when the trigger sits alongside buttons of a size
   * the glyph ramp can't express (e.g. `xs`); otherwise it follows `icon.size`.
   */
  size?: ButtonSize;
  /**
   * Visible trigger copy. When set, the trigger renders as a labeled button
   * with the icon beside the text instead of an icon-only button. Pair with
   * `icon.position: 'right'` for a caret that trails the label.
   */
  text?: string;
  /** Trigger tooltip. Defaults to the trigger's accessible name. */
  tooltip?: SharedTooltipProps;
};

export const ActionsMenu = memo(function ActionsMenu({
  actions,
  icon,
  groupedActions,
  id,
  label,
  size,
  text,
  tooltip,
}: ActionsMenuProps) {
  const triggerLabel = icon.label ?? text ?? '';
  const triggerTooltip = tooltip ?? { text: icon.label };
  const triggerSize = size ?? icon.size ?? 'm';

  return (
    <Menu
      as="button"
      groupedMenuItems={groupedActions?.map((group) => ({
        title: group.title,
        items: group.actions,
      }))}
      label={label}
      menuItems={actions}
      trigger={({ getReferenceProps, ref }) => {
        const referenceProps = getReferenceProps();
        /* `useRole` generates an id for the trigger and points the open menu's
           `aria-labelledby` at it, so an unset `id` must not overwrite it. */
        const triggerId = id ?? (referenceProps.id as string | undefined);

        return text === undefined ? (
          <IconButton
            {...referenceProps}
            ref={ref}
            icon={icon}
            id={triggerId}
            label={triggerLabel}
            size={triggerSize}
            tooltip={triggerTooltip}
            variant="tertiary"
          />
        ) : (
          <Button
            {...referenceProps}
            ref={ref}
            icon={icon}
            id={triggerId}
            label={triggerLabel}
            size={triggerSize}
            text={text}
            tooltip={triggerTooltip}
            variant="tertiary"
          />
        );
      }}
    />
  );
});

ActionsMenu.displayName = 'ActionsMenu';
